/**
 * Attachment locator richness — the ONE never-downgrade rule for the
 * `attachmentsSerialized` field, shared by every transcript merge.
 *
 * Extracted 2026-09-08 from chatThreadMessageDocFirestore.ts (where the
 * dual-store merge has used it since the 2026-08-20 attachment-durability
 * round) so the group-thread overlay merge can apply the SAME rule.
 * 2026-09-07 owner report: a photo sent to another user in a shared thread
 * vanished when coming back — the recipient's own tree had already persisted
 * a text-only copy of the sender's turn, and the overlay merge let the own
 * copy win wholesale, so the storage-backed attachment the server later
 * wrote to the shared doc could never heal it.
 *
 * Pure; no Firestore SDK.
 */
import type { ChatMessage } from '../components/GlobalChatSystem/types';

/**
 * Locator richness of a message's attachment records (2026-08-20 attachment-
 * durability round). Grades what a copy can actually RENDER or FETCH:
 * each record scores 1 for existing (metadata keeps the tile alive), +1 for
 * an inline dataUrl or gcsPath, +2 for each durable Storage locator. Used to
 * graft the richer attachment set onto whichever copy the rank/richness pick
 * keeps, so a merge can never DOWNGRADE attachments — the exact vanish class
 * where a server-lane copy (dataUrl deliberately stripped) beat the local
 * copy on body richness and the user's chip degraded to a “sync needed”
 * shell, or disappeared outright.
 */
export function attachmentLocatorRichness(m: ChatMessage): number {
  const list = (m as unknown as { attachmentsSerialized?: unknown }).attachmentsSerialized;
  if (!Array.isArray(list) || list.length === 0) return 0;
  let score = 0;
  for (const raw of list) {
    const att = (raw || {}) as Record<string, unknown>;
    score += 1;
    if (typeof att.dataUrl === 'string' && att.dataUrl) score += 1;
    if (typeof att.gcsPath === 'string' && att.gcsPath) score += 1;
    if (typeof att.storagePath === 'string' && att.storagePath) score += 2;
    if (typeof att.storageUrl === 'string' && att.storageUrl) score += 2;
  }
  return score;
}

/** Winner keeps its body/metadata; the LOSER's attachment set rides along
 *  whenever it is locator-richer. Field-level never-downgrade for the one
 *  field the two stores are KNOWN to disagree on by design (the server lane
 *  strips dataUrls; the local lane may lack the storage upgrade). Returns the
 *  SAME `winner` reference when nothing is grafted, so callers can identity-
 *  check for "unchanged". */
export function graftRicherAttachments(winner: ChatMessage, loser: ChatMessage): ChatMessage {
  if (attachmentLocatorRichness(loser) > attachmentLocatorRichness(winner)) {
    return {
      ...winner,
      attachmentsSerialized: (loser as unknown as { attachmentsSerialized?: unknown })
        .attachmentsSerialized,
    } as ChatMessage;
  }
  return winner;
}
